/**
 * Supervising a live call (V1-E04-T04): listen, whisper or barge from the browser, a WebRTC call through
 * the edge with a join credential minutes long. Shown to those who may monitor (calls:monitor: owners,
 * admins, operators). Listen: both sides heard, nobody hears you. Whisper: the person the caller is
 * bridged to hears you, the caller does not. Barge: both do. The mode changes during the session.
 */
import { connect, type Call, type CallState } from "@swaralo/widget";
import { Headphones } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { data } from "../api/client";
import { useBff, useCan } from "../app/session";
import { useT } from "../i18n";
import { Button } from "../ui/Button";

type Mode = "listen" | "whisper" | "barge";
const MODES: Mode[] = ["listen", "whisper", "barge"];
/** The keypad digit that switches an eavesdrop to each mode (measured on FreeSWITCH). */
export const MODE_DIGIT: Record<Mode, string> = { listen: "0", whisper: "1", barge: "3" };

export function Monitor({ callId, join = connect }: { callId: string; join?: typeof connect }) {
  const t = useT();
  const { api } = useBff();
  const allowed = useCan("calls:monitor");
  const [mode, setMode] = useState<Mode | null>(null);
  const [state, setState] = useState<CallState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const call = useRef<Call | null>(null);
  useEffect(() => () => void call.current?.hangUp(), []);
  if (!allowed) return null;

  async function choose(next: Mode) {
    setError(null);
    if (call.current && (state === "in-call" || state === "ringing")) {
      call.current.dtmf(MODE_DIGIT[next]); // the same session, another mode
      setMode(next);
      return;
    }
    try {
      const login = await data(api.POST("/v1/calls/{call_id}/monitor", {
        params: { path: { call_id: callId } }, body: { mode: next },
      }));  // prettier-ignore
      setMode(next);
      call.current = await join(login, {
        onState: (s) => setState(s),
        onEnd: () => {
          call.current = null;
          setMode(null);
        },
      });
    } catch (e) {
      setMode(null);
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const joined = state === "in-call" || state === "ringing" || state === "connecting";
  return (
    <section aria-labelledby="live-monitor" className="space-y-2 rounded-lg border border-[var(--color-border)] p-4">
      <h2 id="live-monitor" className="flex items-center gap-2 font-semibold">
        <Headphones aria-hidden className="size-4" /> {t("monitor.title")}
      </h2>
      <p className="text-sm text-[var(--color-muted)]">{t("monitor.help")}</p>
      <div role="group" aria-label={t("monitor.title")} className="flex flex-wrap gap-2">
        {MODES.map((m) => (
          <Button key={m} variant={mode === m ? "primary" : "secondary"} aria-pressed={mode === m}
                  onClick={() => void choose(m)}>
            {t(`monitor.${m}`)}
          </Button>
        ))}
        {joined && (
          <Button variant="secondary" onClick={() => void call.current?.hangUp()}>{t("monitor.leave")}</Button>
        )}
      </div>
      {state && <p role="status" className="text-sm">{t(`monitor.state.${state}`)}</p>}
      {error && <p role="alert" className="text-sm text-[var(--color-danger)]">{error}</p>}
    </section>
  );
}
